/**
 * Telas de senha: esqueci, redefinir e definição no primeiro acesso.
 *
 * As travas reais são do servidor: `usuario_operacional` devolve 403 a qualquer
 * operação enquanto a senha for temporária, e o token de redefinição é de uso único
 * e expira. Estas telas são conveniência — sem elas o usuário veria 403 e não
 * saberia por quê.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthLayout } from '@/components/AuthLayout';
import { CampoSenha } from '@/components/CampoSenha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { useSessao } from '@/lib/auth';

const MINIMO = 10;

/** Nova senha + confirmação. Devolve o erro de divergência, se houver. */
function useNovaSenha() {
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const divergem = confirmacao.length > 0 && senha !== confirmacao;
  const valida = senha.length >= MINIMO && senha === confirmacao;
  return { senha, setSenha, confirmacao, setConfirmacao, divergem, valida };
}

function CamposNovaSenha({ f }: { f: ReturnType<typeof useNovaSenha> }) {
  return (
    <>
      <CampoSenha
        id="nova"
        rotulo="Nova senha"
        autoComplete="new-password"
        minLength={MINIMO}
        valor={f.senha}
        onChange={f.setSenha}
        autoFocus
        ajuda={`Mínimo de ${MINIMO} caracteres. Prefira uma frase longa a símbolos difíceis de lembrar.`}
      />
      <CampoSenha
        id="confirmacao"
        rotulo="Confirme a nova senha"
        autoComplete="new-password"
        valor={f.confirmacao}
        onChange={f.setConfirmacao}
        erro={f.divergem ? 'As senhas não coincidem.' : undefined}
      />
    </>
  );
}

// --------------------------------------------------------------------------

export function Esqueci() {
  const navegar = useNavigate();
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    // O servidor responde igual exista o e-mail ou não, e a tela faz o mesmo:
    // confirmar aqui o que o servidor esconde entregaria a lista de usuários.
    await api.post('/auth/esqueci', { email });
    setEnviando(false);
    setEnviado(true);
  }

  if (enviado) {
    return (
      <AuthLayout subtitulo="Verifique seu e-mail">
        <div className="rounded-lg border border-border bg-surface p-6 text-[13px] leading-normal text-text-secondary">
          <p>
            Se <span className="tipo-dado text-text">{email}</span> estiver cadastrado, enviamos um
            link de redefinição. Ele expira em 30 minutos e só funciona uma vez.
          </p>
          <Button variant="fantasma" className="mt-4 w-full" onClick={() => navegar('/login')}>
            Voltar ao login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout subtitulo="Recuperar acesso">
      <form onSubmit={enviar} className="rounded-lg border border-border bg-surface p-6">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              className="font-mono"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button type="submit" variant="primario" className="w-full" disabled={enviando}>
            {enviando ? <Spinner /> : 'Enviar link de redefinição'}
          </Button>
          <Button type="button" variant="fantasma" className="w-full" onClick={() => navegar('/login')}>
            Voltar
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

// --------------------------------------------------------------------------

export function Redefinir() {
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const f = useNovaSenha();
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    const r = await api.post('/auth/redefinir', { token, nova_senha: f.senha });
    setEnviando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    toast.success('Senha redefinida. Entre com ela.');
    navegar('/login', { replace: true });
  }

  if (!token) {
    return (
      <AuthLayout subtitulo="Link inválido">
        <div className="rounded-lg border border-border bg-surface p-6 text-[13px] leading-normal text-text-secondary">
          <p>Este link não tem um token de redefinição. Peça um novo.</p>
          <Button variant="fantasma" className="mt-4 w-full" onClick={() => navegar('/esqueci')}>
            Pedir novo link
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout subtitulo="Defina uma nova senha">
      <form onSubmit={enviar} className="rounded-lg border border-border bg-surface p-6">
        <div className="flex flex-col gap-5">
          <CamposNovaSenha f={f} />
          <Button type="submit" variant="primario" className="w-full" disabled={enviando || !f.valida}>
            {enviando ? <Spinner /> : 'Redefinir senha'}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

// --------------------------------------------------------------------------

/**
 * Primeiro acesso.
 *
 * Não pedimos a senha temporária de novo: ela foi digitada no login que criou esta
 * sessão, e o segundo fator já provou posse do e-mail e do WhatsApp. Pedi-la seria
 * ritual, não segurança — e um ritual que empurra as pessoas a copiar e colar do
 * e-mail. O servidor concorda: `senha_atual` é dispensada quando `deve_trocar_senha`.
 *
 * Ao gravar, a sessão continua viva e o usuário segue direto para a área logada.
 * As demais sessões dele caem.
 */
export function TrocarSenha() {
  const { recarregar } = useSessao();
  const navegar = useNavigate();
  const f = useNovaSenha();
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    const r = await api.post('/auth/senha', { nova_senha: f.senha });
    setEnviando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    await recarregar(); // `deve_trocar_senha` agora é falso; o Guarda libera.
    toast.success('Senha definida.');
    navegar('/', { replace: true });
  }

  return (
    <AuthLayout subtitulo="Defina sua senha">
      <form onSubmit={enviar} className="rounded-lg border border-border bg-surface p-6">
        <div className="flex flex-col gap-5">
          <p className="text-[13px] leading-normal text-text-secondary">
            A senha que você recebeu por e-mail serviu para o primeiro acesso. Como ela trafegou por
            e-mail, escolha agora uma senha que só você conhece.
          </p>
          <CamposNovaSenha f={f} />
          <Button type="submit" variant="primario" className="w-full" disabled={enviando || !f.valida}>
            {enviando ? <Spinner /> : 'Definir senha e entrar'}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
